// home-banner.js
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
const isEditor = !!document.body.classList.contains("wp-admin");
if (!isEditor) gsap.registerPlugin(ScrollTrigger);

// ---------- helpers ----------
function parseNumber(str) {
  const s = (str || "").trim();
  const m = s.match(/^([^0-9\-+]*)([\d.,]+)(.*)$/);
  if (!m) return { value: 0, prefix: "", suffix: "" };
  const prefix = m[1] || "";
  const numStr = m[2].replace(/,/g, "");
  const suffix = m[3] || "";
  return { value: parseFloat(numStr) || 0, prefix, suffix };
}

function animateNumber(el, { end, prefix = "", suffix = "", duration = 1.8 }) {
  const obj = { val: 0 };
  gsap.to(obj, {
    val: end,
    duration,
    ease: "expo.out",
    snap: { val: 1 },
    onUpdate: () => {
      el.textContent = `${prefix}${obj.val.toLocaleString("en-US")}${suffix}`;
    },
  });
}

// ---------- main init (one banner instance) ----------
function initHomeBanner(banner) {
  if (!banner || banner.__hbInit) return;
  banner.__hbInit = true;

  const content = banner.querySelector(".home-banner__content");
  const eyebrown = banner.querySelector(".banner__eyebrow");
  const headline = banner.querySelector(".home-banner__headline");
  const subheadline = banner.querySelector(".home-banner__subheadline");
  const ctas = Array.from(banner.querySelectorAll(".btn--accent"));
  const seoImg = banner.querySelector(".home-banner__seo-image img");
  const statsWrap = banner.querySelector(".home-banner__stats");
  const statCards = Array.from(banner.querySelectorAll(".home-banner__stat"));

  // prepare numbers (preserve + / units)
  statCards.forEach((card) => {
    const numEl = card.querySelector(".home-banner__stat-number");
    if (!numEl) return;
    const parsed = parseNumber(numEl.textContent);
    numEl.dataset.target = String(parsed.value);
    numEl.dataset.prefix = parsed.prefix;
    numEl.dataset.suffix = parsed.suffix;
    numEl.textContent = `${parsed.prefix}0${parsed.suffix}`;
  });

  if (isEditor) {
    // ---- Editor: simple entrance, no scroll triggers ----
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

    if (eyebrown) {
      tl.fromTo(
        eyebrown,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6 },
      );
    }

    if (headline) {
      tl.fromTo(
        headline,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6 },
      );
    }

    if (subheadline) {
      tl.fromTo(
        subheadline,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5 },
        "-=0.3",
      );
    }

    if (ctas.length) {
      tl.fromTo(
        ctas,
        { y: 15, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.45, stagger: 0.12 },
        "-=0.25",
      );
    }

    if (seoImg) {
      tl.fromTo(
        seoImg,
        { x: 20, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.6 },
        "-=0.35",
      );
    }

    // numbers count once (gentle)
    statCards.forEach((card, i) => {
      const el = card.querySelector(".home-banner__stat-number");
      if (!el) return;
      gsap.set(card, { opacity: 0, y: 16 });
      gsap.to(card, {
        opacity: 1,
        y: 0,
        duration: 0.45,
        delay: 0.1 * i,
        ease: "power2.out",
      });
      animateNumber(el, {
        end: parseFloat(el.dataset.target),
        prefix: el.dataset.prefix || "",
        suffix: el.dataset.suffix || "",
        duration: 1.4,
      });
    });
    return;
  }

  // ---- Frontend: staggered entrance + ScrollTrigger ----
  const heroTl = gsap.timeline({
    defaults: { ease: "power3.out" },
    scrollTrigger: { trigger: banner, start: "top 80%", once: true },
  });

  if (eyebrown) {
    heroTl.fromTo(
      eyebrown,
      { y: 40, opacity: 0, filter: "blur(4px)" },
      { y: 0, opacity: 1, filter: "blur(0px)", duration: 0.9 },
    );
  }

  if (headline) {
    heroTl.fromTo(
      headline,
      { y: 40, opacity: 0, filter: "blur(4px)" },
      { y: 0, opacity: 1, filter: "blur(0px)", duration: 0.9 },
    );
  }

  if (subheadline) {
    heroTl.fromTo(
      subheadline,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.7 },
      "-=0.5",
    );
  }

  if (ctas.length) {
    heroTl.fromTo(
      ctas,
      { y: 20, opacity: 0, scale: 0.96 },
      {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.6,
        ease: "back.out(1.6)",
        stagger: 0.12,
      },
      "-=0.4",
    );
  }

  if (seoImg) {
    heroTl.fromTo(
      seoImg,
      { x: 60, y: 10, opacity: 0, rotate: -1.5 },
      { x: 0, y: 0, opacity: 1, rotate: 0, duration: 1.0 },
      "-=0.6",
    );

    gsap.to(seoImg, {
      y: 10,
      duration: 3.2,
      yoyo: true,
      repeat: -1,
      ease: "sine.inOut",
      scrollTrigger: {
        trigger: banner,
        start: "top bottom",
        end: "bottom top",
        toggleActions: "play pause resume pause",
      },
    });
  }

  if (statCards.length) {
    gsap.set(statCards, { opacity: 0, y: 24 });

    const statsTl = gsap.timeline({
      defaults: { ease: "power2.out" },
      scrollTrigger: {
        trigger: statsWrap || statCards[0],
        start: "top 85%",
        once: true,
      },
    });

    statsTl.to(statCards, {
      opacity: 1,
      y: 0,
      duration: 0.6,
      stagger: 0.12,
    });

    statsTl.add(() => {
      statCards.forEach((card) => {
        const el = card.querySelector(".home-banner__stat-number");
        if (!el) return;
        animateNumber(el, {
          end: parseFloat(el.dataset.target),
          prefix: el.dataset.prefix || "",
          suffix: el.dataset.suffix || "",
          duration: 1.8,
        });
      });
    }, "<");
  }
}

// ---------- mount ----------
const mount = () => {
  if (!isEditor) {
    document.querySelectorAll(".home-banner").forEach(initHomeBanner);
    return;
  }

  const root = document.querySelector(".editor-styles-wrapper") || document;
  const run = () =>
    root.querySelectorAll(".home-banner").forEach(initHomeBanner);
  run();

  const mo = new MutationObserver(() => run());
  mo.observe(root, { childList: true, subtree: true });
};

if (window.wp?.domReady) {
  window.wp.domReady(mount);
} else {
  document.addEventListener("DOMContentLoaded", mount);
}
